import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowDown, Square, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface FlowchartNode {
  id: string;
  type: 'start' | 'end' | 'process' | 'decision';
  label: string;
  description?: string;
  x: number;
  y: number;
}

interface FlowchartEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
}

interface FlowchartData {
  nodes: FlowchartNode[];
  edges: FlowchartEdge[];
}

interface WorkProcessFlowchartProps {
  flowchartData: FlowchartData;
  onChange?: (data: FlowchartData) => void;
  readOnly?: boolean;
}

const NODE_TYPES = [
  { type: 'start', label: 'Start', color: 'bg-green-500' },
  { type: 'process', label: 'Schritt', color: 'bg-blue-500' },
  { type: 'decision', label: 'Entscheidung', color: 'bg-amber-500' },
  { type: 'end', label: 'Ende', color: 'bg-red-500' },
] as const;

const WorkProcessFlowchart = ({ flowchartData, onChange, readOnly = false }: WorkProcessFlowchartProps) => {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<string | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNodeExpanded = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const addNode = (type: FlowchartNode['type']) => {
    if (readOnly || !onChange) return;
    
    const newNode: FlowchartNode = {
      id: `node-${Date.now()}`,
      type,
      label: type === 'start' ? 'Start' : type === 'end' ? 'Ende' : type === 'decision' ? 'Bedingung?' : 'Neuer Schritt',
      description: type === 'process' ? '1. Erste Aktion\n2. Zweite Aktion\n3. Dritte Aktion' : '',
      x: 200,
      y: (flowchartData.nodes.length * 120) + 50,
    };
    
    onChange({
      ...flowchartData,
      nodes: [...flowchartData.nodes, newNode],
    });
  };

  const updateNodeLabel = (nodeId: string, label: string) => {
    if (readOnly || !onChange) return;
    
    onChange({
      ...flowchartData,
      nodes: flowchartData.nodes.map(n => 
        n.id === nodeId ? { ...n, label } : n
      ),
    });
  };

  const updateNodeDescription = (nodeId: string, description: string) => {
    if (readOnly || !onChange) return;
    
    onChange({
      ...flowchartData,
      nodes: flowchartData.nodes.map(n => 
        n.id === nodeId ? { ...n, description } : n
      ),
    });
  };

  const deleteNode = (nodeId: string) => {
    if (readOnly || !onChange) return;
    
    onChange({
      nodes: flowchartData.nodes.filter(n => n.id !== nodeId),
      edges: flowchartData.edges.filter(e => e.from !== nodeId && e.to !== nodeId),
    });
    setSelectedNode(null);
  };

  const handleNodeClick = (nodeId: string) => {
    if (connectingFrom) {
      if (connectingFrom !== nodeId && onChange) {
        const newEdge: FlowchartEdge = {
          id: `edge-${Date.now()}`,
          from: connectingFrom,
          to: nodeId,
        };
        onChange({
          ...flowchartData,
          edges: [...flowchartData.edges, newEdge],
        });
      }
      setConnectingFrom(null);
    } else {
      setSelectedNode(nodeId === selectedNode ? null : nodeId);
    }
  };

  const deleteEdge = (edgeId: string) => {
    if (readOnly || !onChange) return;
    
    onChange({
      ...flowchartData,
      edges: flowchartData.edges.filter(e => e.id !== edgeId),
    });
  };

  const getNodeStyle = (type: FlowchartNode['type']) => {
    switch (type) {
      case 'start':
        return 'rounded-full bg-green-500/20 border-green-500 text-green-700 dark:text-green-300';
      case 'end':
        return 'rounded-full bg-red-500/20 border-red-500 text-red-700 dark:text-red-300';
      case 'decision':
        return 'rotate-45 bg-amber-500/20 border-amber-500';
      case 'process':
      default:
        return 'rounded-lg bg-blue-500/20 border-blue-500 text-blue-700 dark:text-blue-300';
    }
  };

  const hasDescription = (node: FlowchartNode) => {
    return node.description && node.description.trim().length > 0;
  };

  return (
    <div className="space-y-4 p-4">
      {!readOnly && (
        <div className="flex flex-wrap gap-2 pb-4 border-b border-border">
          <span className="text-sm text-muted-foreground mr-2 self-center">Element hinzufügen:</span>
          {NODE_TYPES.map(({ type, label, color }) => (
            <Button
              key={type}
              variant="outline"
              size="sm"
              onClick={() => addNode(type)}
              className="gap-2"
            >
              <div className={cn("w-3 h-3 rounded-sm", color)} />
              {label}
            </Button>
          ))}
          
          {connectingFrom && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-amber-600">Verbindungsziel wählen...</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setConnectingFrom(null)}
              >
                Abbrechen
              </Button>
            </div>
          )}
        </div>
      )}

      {flowchartData.nodes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Square className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Kein Flowchart vorhanden</h3>
          <p className="text-muted-foreground mb-4">
            {readOnly 
              ? "Für diesen Prozess wurde noch kein Flowchart erstellt."
              : "Fügen Sie Elemente hinzu, um das Flowchart zu erstellen, oder nutzen Sie den KI-Generator."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {flowchartData.nodes.map((node, index) => {
            const incomingEdges = flowchartData.edges.filter(e => e.to === node.id);
            const isExpanded = expandedNodes.has(node.id);
            const nodeHasDescription = hasDescription(node);
            
            return (
              <div key={node.id} className="relative">
                {/* Incoming arrows */}
                {incomingEdges.length > 0 && index > 0 && (
                  <div className="flex justify-center py-2">
                    <ArrowDown className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                
                {/* Node */}
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "relative flex items-center justify-center mx-auto p-4 border-2 min-w-[200px] max-w-[350px] cursor-pointer transition-all",
                      getNodeStyle(node.type),
                      selectedNode === node.id && "ring-2 ring-primary ring-offset-2",
                      connectingFrom && "hover:ring-2 hover:ring-amber-400"
                    )}
                    onClick={() => handleNodeClick(node.id)}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-full gap-2",
                      node.type === 'decision' && "-rotate-45"
                    )}>
                      {nodeHasDescription && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleNodeExpanded(node.id);
                          }}
                          className="flex-shrink-0"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      {editingLabel === node.id && !readOnly ? (
                        <Input
                          value={node.label}
                          onChange={(e) => updateNodeLabel(node.id, e.target.value)}
                          onBlur={() => setEditingLabel(null)}
                          onKeyDown={(e) => e.key === 'Enter' && setEditingLabel(null)}
                          autoFocus
                          className="text-center bg-transparent border-none h-auto p-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span 
                          className="text-sm font-medium text-center cursor-text"
                          onDoubleClick={(e) => {
                            if (!readOnly) {
                              e.stopPropagation();
                              setEditingLabel(node.id);
                            }
                          }}
                        >
                          {node.label}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded description panel */}
                  {isExpanded && nodeHasDescription && (
                    <div className="mt-2 w-full max-w-[450px] bg-muted/50 border border-border rounded-lg p-4">
                      {editingDescription === node.id && !readOnly ? (
                        <Textarea
                          value={node.description || ''}
                          onChange={(e) => updateNodeDescription(node.id, e.target.value)}
                          onBlur={() => setEditingDescription(null)}
                          rows={6}
                          className="text-sm"
                          placeholder="Schritt-für-Schritt-Anweisungen..."
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <div 
                          className="text-sm text-foreground whitespace-pre-line cursor-text"
                          onDoubleClick={(e) => {
                            if (!readOnly) {
                              e.stopPropagation();
                              setEditingDescription(node.id);
                            }
                          }}
                        >
                          {node.description}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions for selected node */}
                {selectedNode === node.id && !readOnly && (
                  <div className="flex justify-center gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConnectingFrom(node.id)}
                      className="text-xs"
                    >
                      Verbinden
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingLabel(node.id)}
                      className="text-xs"
                    >
                      Titel
                    </Button>
                    {(node.type === 'process' || node.type === 'decision') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (!expandedNodes.has(node.id)) {
                            toggleNodeExpanded(node.id);
                          }
                          setEditingDescription(node.id);
                        }}
                        className="text-xs"
                      >
                        Anweisungen
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteNode(node.id)}
                      className="text-xs"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edge list for management */}
      {!readOnly && flowchartData.edges.length > 0 && (
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-medium mb-2">Verbindungen:</h4>
          <div className="flex flex-wrap gap-2">
            {flowchartData.edges.map((edge) => {
              const fromNode = flowchartData.nodes.find(n => n.id === edge.from);
              const toNode = flowchartData.nodes.find(n => n.id === edge.to);
              return (
                <div 
                  key={edge.id}
                  className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded"
                >
                  <span>{fromNode?.label}</span>
                  <ArrowDown className="h-3 w-3" />
                  <span>{toNode?.label}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1"
                    onClick={() => deleteEdge(edge.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkProcessFlowchart;